import type { ComponentPropsWithRef, ReactNode } from "react";

import { cn } from "../lib/cn";
import { controlBase, controlOrnament, controlSizes, type ControlSize } from "../lib/control";
import { useFieldControl } from "./field";

export interface InputProps extends Omit<ComponentPropsWithRef<"input">, "size"> {
  size?: ControlSize;
  /** Icône ou symbole affiché à gauche (loupe, « € »…). */
  leading?: ReactNode;
  /** Icône ou action affichée à droite. */
  trailing?: ReactNode;
}

/**
 * Champ de saisie sur une ligne.
 *
 * @example
 * <Field label="Recherche">
 *   <Input leading={<SearchIcon />} placeholder="Rechercher…" />
 * </Field>
 */
export function Input({ className, size = "md", leading, trailing, ...props }: InputProps) {
  // `fieldProps` reprend le reste des props et y ajoute le câblage aria du Field parent.
  const fieldProps = useFieldControl(props);
  const ornament = controlOrnament[size];

  const input = (
    <input
      className={cn(
        controlBase,
        controlSizes[size],
        // Les ornements sont posés en absolu : on réserve la place par du padding
        // plutôt que par un conteneur flex, sinon le curseur texte ne couvre plus
        // toute la largeur cliquable du champ.
        leading && ornament.leading,
        trailing && ornament.trailing,
        // `peer` : les ornements sont des FRÈRES SUIVANTS de l'input dans le DOM
        // (leur position est absolue, l'ordre visuel ne dépend pas de l'ordre du
        // balisage). C'est ce qui leur permet de réagir au focus du champ.
        "peer",
        className,
      )}
      {...fieldProps}
    />
  );

  if (!leading && !trailing) return input;

  return (
    <div className="relative flex items-center">
      {input}

      {/* L'ornement de tête ne doit jamais se lire comme le premier caractère de
          la saisie. Deux choses l'en distinguent : l'écart (voir `controlOrnament`)
          et la couleur — l'icône reste plus pâle que le texte au repos, et vire à
          l'accent dès que le champ est actif. Elle appartient alors visiblement au
          contrôle, pas à son contenu. */}
      {leading && (
        <span
          className={cn(
            "pointer-events-none absolute flex text-fg-subtle/70 [&_svg]:size-4",
            "transition-colors duration-fast ease-standard peer-focus:text-accent",
            ornament.leadingInset,
          )}
          aria-hidden="true"
        >
          {leading}
        </span>
      )}

      {trailing && (
        <span className={cn("absolute flex text-fg-subtle [&_svg]:size-4", ornament.trailingInset)}>
          {trailing}
        </span>
      )}
    </div>
  );
}
